import {
    MobileWidgetCardsContainerStyled,
    MobileWidgetCardsHeaderStyled,
    MobileWidgetCardsSeeAllContainerStyled,
    MobileWidgetCardsSeeAllStyled,
    MobileWidgetCardsStyled,
} from './styled/MobileWidgetCardsStyled';
import { useLangContext } from '../../../../providers/LangProvider/LangProviderContext';
import BinIcon from '../../../../components/icon/Icon';
import React, { useCallback } from 'react';
import type { ILiteTradeAssetSymbol } from '../../../../modules/LiteTrade/types/liteTradeReducer.t';
import useMobileWidgetCardsAssets from './useMobileWidgetCardsAssets';
import useHorizontalScroll from '../../../../hooks/useHorizontalScroll';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { getIsLocationPlatformMobileSelector } from '../../../../modules/Location/locationSelectors';
import ButtonMove from '../../../../components/ButtonMove/ButtonMove';

interface IMobileWidgetCardsChildrenProps {
    key: string,
    disabled?: boolean,
    symbol: ILiteTradeAssetSymbol,
}

interface IMobileWidgetCardsProps<P> {
    assetsCount: number,
    className?: string,
    collectionKey: string,
    disabled?: boolean,
    withSeeAll?: boolean,
    widgetCardItem: React.FC<IMobileWidgetCardsChildrenProps & P>,
    widgetCardItemProps: P,
    widgetKey?: string,
}

function MobileWidgetCards<P>(props: IMobileWidgetCardsProps<P>) {
    const {
        widgetCardItem: WidgetCardItem,
        widgetCardItemProps,
        collectionKey,
        assetsCount,
        widgetKey,
        className,
        withSeeAll = true,
        disabled,
    } = props;
    const { getLangPath } = useLangContext();
    const {
        assets: assetsToRender,
        collectionTitle,
        openCollectionSlide,
    } = useMobileWidgetCardsAssets(collectionKey, assetsCount);
    const handleSeeAllClick = useCallback(() => openCollectionSlide(widgetKey), [openCollectionSlide, widgetKey]);
    const isPlatformMobile = useSelector(getIsLocationPlatformMobileSelector);
    const {
        scrollRef,
        onScrollLeft,
        onScrollRight,
        props: mouseProps,
        isDragging,
        isOverflowing,
    } = useHorizontalScroll<HTMLDivElement>();

    return (
        <MobileWidgetCardsStyled show={assetsToRender.length > 0} className={classNames(className, { disabled })}>
            <MobileWidgetCardsHeaderStyled>
                <h5 className={'card-title mb-0'}>{collectionTitle}</h5>
                {withSeeAll && <MobileWidgetCardsSeeAllContainerStyled onClick={handleSeeAllClick}>
                    <MobileWidgetCardsSeeAllStyled>
                        <span className={'text-secondary'}>{getLangPath('lite_version.market.see_all')}</span>
                        <BinIcon name={'IcForward'} className={'ms-2 arrow-icon'}/>
                    </MobileWidgetCardsSeeAllStyled>
                </MobileWidgetCardsSeeAllContainerStyled>}
            </MobileWidgetCardsHeaderStyled>
            <MobileWidgetCardsContainerStyled ref={scrollRef} {...mouseProps} className={classNames({
                'custom-scrollbar': !isPlatformMobile,
                'hidden-scrollbar': isPlatformMobile,
                mobile: isPlatformMobile,
            })}>
                {assetsToRender.map(asset => <WidgetCardItem key={asset.symbol}
                                                             symbol={asset.symbol}
                                                             disabled={isDragging}
                                                             {...(widgetCardItemProps)} />)}
            </MobileWidgetCardsContainerStyled>

            {
                !isPlatformMobile && isOverflowing &&
                <>
                    <ButtonMove className={'backdrop-filter-blur100 arrow-left'} direction={'left'} size={'xs'}
                                onClick={onScrollLeft}/>
                    <ButtonMove className={'backdrop-filter-blur100 arrow-right'} direction={'right'} size={'xs'}
                                onClick={onScrollRight}/>
                </>
            }
        </MobileWidgetCardsStyled>
    );
}

export default MobileWidgetCards;
